'use client';

import { CONSULTATION_SERVICE_SLUG } from '@/lib/constants/booking';
import { useOpenAppointmentBooking } from '@/lib/hooks/use-appointment-booking-url';
import Icon from '../../ui/AppIcon';
import { motion } from 'framer-motion';

interface Facility {
  id: string
  icon: string
  title: string
  description: string
}

export default function FacilitiesSection() {
  const openBooking = useOpenAppointmentBooking()

  const facilities: Facility[] = [
    {
      id: 'facility_premium_products',
      icon: 'SparklesIcon',
      title: 'Hair Wellness Focused',
      description: 'Every service is designed to improve the long-term health of your natural hair—not just how it looks today.',
    },
    {
      id: 'facility_expert_stylists',
      icon: 'SparklesIcon',
      title: 'Hair Growth and Length Retention',
      description: 'We guide you on how to grow and retain healthy hair through our consultations, personalized care, and proven hair care practices.',
    },
    {
      id: 'facility_comfortable_space',
      icon: 'SparklesIcon',
      title: 'Gentle Styling—No extensions',
      description: 'We prove to you, that your natural hair is beautiful enough. Our gentle styling techniques protect every strand.',
    },
  ]

  return (
    <section id="facilities" className="py-20 md:py-32 bg-[rgb(var(--color-primary))] text-primary-foreground relative overflow-hidden reveal">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            EXPERIENCE THE
            <br />
            MEVOLY DIFFERENCE
          </h2>
          <p className="text-lg text-primary-foreground/80">
            We believe in providing you with the best possible hair care in a comfortable and modern salon environment.
          </p>
        </motion.div>

        {/* Facilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {facilities.map((facility, index) => (
            <motion.div
              key={facility.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="text-center group"
            >
              {/* Icon */}
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="w-20 h-20 mx-auto mb-6 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:bg-white/20 transition-all duration-300"
              >
                <Icon name={facility.icon as string} size={40} className="text-primary-foreground" />
              </motion.div>

              {/* Content */}
              <h3 className="text-2xl font-bold mb-4 group-hover:scale-105 transition-transform">
                {facility.title}
              </h3>
              <p className="text-primary-foreground/80 leading-relaxed">
                {facility.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() =>
              openBooking({ serviceSlugs: [CONSULTATION_SERVICE_SLUG] })
            }
            className="bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-xl font-semibold shadow-lg transition-all cursor-pointer"
          >
            Book Your Appointment
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}
